import type { CallScript } from "@frontierx/shared/schemas/CallScript";
import { Loader2, Phone } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { CompatibleSocket } from "~/app/types/CompatibleSocket";
import { CallScriptSelector } from "../Script/CallScriptSelector";

interface StartCallModalProps {
  onCreateCall?: (url: string, callScript: CallScript) => void;
  mainSocket?: CompatibleSocket;
  children?: React.ReactNode;
  isCreating?: boolean;
}

export function StartCallModal({
  onCreateCall,
  mainSocket: _mainSocket,
  children,
  isCreating = false,
}: Readonly<StartCallModalProps>) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [selectedScript, setSelectedScript] = useState<CallScript | null>(null);
  const [isValidUrl, setIsValidUrl] = useState(false);

  const validateUrl = useMemo(() => {
    return (url: string) => {
      try {
        new URL(url.trim());
        return true;
      } catch {
        return false;
      }
    };
  }, []);

  useEffect(() => {
    setIsValidUrl(validateUrl(url));
  }, [url, validateUrl]);

  const handleCreateCall = async () => {
    if (!isValidUrl) {
      return;
    }

    if (!selectedScript) {
      return;
    }
    onCreateCall?.(url.trim(), selectedScript);

    // Reset form and close modal
    setUrl("");
    setSelectedScript(null);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children || (
          <Button variant="outline" className="flex items-center gap-2">
            <Phone className="h-4 w-4" />
            Join call
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Start an ad hoc call</DialogTitle>
          <DialogDescription>
            Enter a Google Meet, Teams or Zoom URL. The AI assistant will join
            and provide real-time feedback.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="meeting-url">Meeting URL</Label>
            <Input
              id="meeting-url"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://meet.google.com/..."
              disabled={isCreating}
            />
          </div>

          <div className="space-y-2">
            <Label>Script</Label>
            <div className="mt-3">
              <CallScriptSelector
                selectedScript={selectedScript}
                onSelect={(callScript: CallScript) =>
                  setSelectedScript(callScript)
                }
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateCall}
            disabled={isCreating || !isValidUrl || !selectedScript}
          >
            {isCreating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <Phone className="mr-2 h-4 w-4" />
                Start Call
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
